/**
 * +prof: gathering channel clips (docs/design/professions.md §5). The world frame sets the hero's
 * cast to a channel with abilityId GATHER_HERB_CLIP / GATHER_MINE_CLIP while a gather runs, so the
 * hero view loops these:
 *   - herb: a kneel, both hands reaching down and plucking in a slow rhythm;
 *   - mine: two-handed overhead swings (a pickaxe stroke every ~0.7 s) with dust at the strike.
 */
import { defineClip } from './clips';
import { registerAbilityClips } from './registry';

export const GATHER_HERB_CLIP = 'gather.herb';
export const GATHER_MINE_CLIP = 'gather.mine';

const kneel = {
  hips: [0.15, 0, 0] as [number, number, number],
  spine: [0.45, 0, 0] as [number, number, number],
  head: [0.35, 0, 0] as [number, number, number],
  leftUpperLeg: [1.35, 0, 0.1] as [number, number, number],
  leftLowerLeg: [2.0] as [number],
  rightUpperLeg: [0.6, 0, -0.1] as [number, number, number],
  rightLowerLeg: [1.7] as [number],
};

const herb = defineClip({
  id: GATHER_HERB_CLIP,
  duration: 1.6,
  loop: true,
  loopFrom: 0.4,
  blendIn: 0.2,
  blendOut: 0.25,
  mask: 'full',
  keys: [
    { t: 0.4, ease: 'out', pose: { ...kneel, leftUpperArm: [1.0, 0.2, 0.2], rightUpperArm: [1.1, -0.2, 0.2], leftLowerArm: [0.6], rightLowerArm: [0.5] }, root: { hop: -0.32 } },
    { t: 0.9, ease: 'inOut', pose: { ...kneel, leftUpperArm: [1.3, 0.3, 0.25], rightUpperArm: [0.7, -0.1, 0.15], leftLowerArm: [0.3], rightLowerArm: [1.3] }, root: { hop: -0.34 } },
    { t: 1.3, ease: 'inOut', pose: { ...kneel, leftUpperArm: [0.8, 0.1, 0.2], rightUpperArm: [1.35, -0.3, 0.25], leftLowerArm: [1.2], rightLowerArm: [0.3] }, root: { hop: -0.33 } },
  ],
});

const swingUp = { spine: [-0.15, 0, 0] as [number, number, number], leftUpperArm: [2.7, 0.2, 0.2] as [number, number, number], rightUpperArm: [2.7, -0.2, 0.2] as [number, number, number], leftLowerArm: [0.6] as [number], rightLowerArm: [0.6] as [number] };
const swingDown = { spine: [0.55, 0, 0] as [number, number, number], head: [0.2, 0, 0] as [number, number, number], leftUpperArm: [0.9, 0.3, 0.1] as [number, number, number], rightUpperArm: [0.9, -0.3, 0.1] as [number, number, number], leftLowerArm: [0.2] as [number], rightLowerArm: [0.2] as [number], leftUpperLeg: [0.35] as [number], rightUpperLeg: [0.15] as [number], leftLowerLeg: [0.5] as [number] };

const mine = defineClip({
  id: GATHER_MINE_CLIP,
  duration: 1.0,
  loop: true,
  loopFrom: 0.3,
  blendIn: 0.12,
  blendOut: 0.2,
  mask: 'full',
  keys: [
    { t: 0.3, ease: 'out', pose: swingUp },
    { t: 0.48, ease: 'in', pose: swingDown, root: { hop: -0.08 } },
    { t: 0.62, ease: 'out', pose: { ...swingDown, spine: [0.5, 0, 0] } },
    { t: 1.0, ease: 'inOut', pose: swingUp },
  ],
  cues: [{ t: 0.48, fx: 'dust', at: 'ground', scale: 0.5 }],
});

let done = false;
/** Register the gathering clips once (idempotent). */
export function registerGatherClips(): void {
  if (done) return;
  done = true;
  registerAbilityClips(GATHER_HERB_CLIP, { channel: herb });
  registerAbilityClips(GATHER_MINE_CLIP, { channel: mine });
}
